import {List, type Map, Set} from 'immutable';
import i18next from 'i18next';
import {type FormState, getFormValues} from 'redux-form';
import React from 'react';
import {css} from 'styled-components';

import createSelector from 'web-app/util/custom-create-selector';
import {type RootState} from 'web-app/react/redux/main-reducer';
import {
    GenericFieldType,
    type GenericFormData,
    type GenericFormField,
    type GenericFieldSection,
    type GenericItem,
} from 'web-app/react/components/form/model';
import {sortByString} from 'web-app/util/sorting';
import {ElementAppearance, SectionTitle} from 'web-app/react/components/form-elements/containers';
import NewLookTheme from 'web-app/styleguide/themes/new-look';
import {media, addHover} from 'web-app/styleguide/utils';
import {s3, s4, getSpacing} from 'web-app/styleguide/spacing';
import PlatformHelper from 'web-app/helpers/platform-helper';
import {hasValue} from 'web-app/util/typescript';
import {immutableSelectorCreator} from 'web-app/util/immutable';
import {Close} from 'web-app/react/components/icons/streamline';
import {AfterTitleElement} from 'web-app/react/components/form-elements/containers/section';
import {Base, Flex} from 'web-app/react/components/layout/layout';
import {Body} from 'web-app/react/components/text/text';

import * as Models from './models';
import * as Actions from './actions';
import * as Constants from './constants';

const filterSections = (state: RootState) => state.lists.newList.filtersAndColumns.filterSections;

const buildCheckboxSection = (entity: Models.FilterEntity, defaultFilters: Map<string, any>): GenericFieldSection[] => {
    return [
        {
            name: entity.id,
            fieldType: GenericFieldType.section,
            fields: entity.values.toArray().map(value => ({
                name: value.id,
                fieldType: GenericFieldType.checkbox,
                text: value.name,
                value:
                    defaultFilters
                        ?.get(entity.id)
                        ?.split(',')
                        .some(id => id === value.id) ?? [],
            })),
        },
    ];
};

const buildDropdowns = (entity: Models.FilterEntity, defaultFilters: Map<string, any>, hasMultipleInputs: boolean) => {
    // This is a bit of a hacky solution, but it's sadly necessary since we want to keep the list app query params
    // compatible with the old list app. Every filter that requires multiple values will have its values stored as
    // a comma seperated string for this reason.
    const defaultFilterValues = defaultFilters.get(entity.id)?.split(',');
    return entity.values.toArray().map<GenericFormField>((value, index) => ({
        name: hasMultipleInputs ? value.name || value.id : entity.id,
        fieldType: GenericFieldType.select,
        label: hasMultipleInputs ? value.name : entity.name,
        placeholder: null,
        value: defaultFilterValues && defaultFilterValues.length > index ? defaultFilterValues[index] : null,
        options: value.values.toArray().map(value => ({
            value: value.id,
            label: value.name,
        })),
        appearance: [ElementAppearance.Toolbar],
        props: {
            // Allows to initialize form fields with 'null',
            // so all form fields can be accessed
            format: value => value,
        },
        elementProps: {
            fieldHolderCss: css`
                // Makes border visible on the white background for mobile
                &&& .Select-control {
                    ${media.mobile`
                            border-color: ${NewLookTheme.borderConfiguration.defaultColor} !important;
                        `}
                }
            `,
        },
    }));
};

const buildDropdownSection = (entity: Models.FilterEntity, defaultFilters: Map<string, any>): GenericFieldSection[] => {
    return [
        {
            name: entity.id,
            fieldType: GenericFieldType.section,
            fields: buildDropdowns(entity, defaultFilters, true),
        },
    ];
};

const getFilterSectionFields = (entity: Models.FilterEntity, defaultFilters: Map<string, any>) => {
    const hasMultipleInputs = entity.values.size > 1;

    // The section contains a group of checkboxes
    if (!isDropdowns(entity.filter)) {
        return buildCheckboxSection(entity, defaultFilters);
    }

    // If a filter entity contains multiple `values`,
    // its filter value is composed of values from multiple form fields.
    // E.g., the filter entity `olderthan` has 2 values/fields (year, month),
    // therefore its value will be `{olderthan: year, month}`.
    return hasMultipleInputs
        ? buildDropdownSection(entity, defaultFilters)
        : buildDropdowns(entity, defaultFilters, hasMultipleInputs);
};

const isDropdowns = (filterType: Models.FilterType) => {
    return filterType === Models.FilterType.Dropdown;
};

const filtersFromQueryParamsOrDefault = immutableSelectorCreator(
    (_: RootState, props: {filtersFromQueryParams?: Map<string, string>}) => props.filtersFromQueryParams,
    (state: RootState) => state.lists.newList.filtersAndColumns.defaultFilters,
    (filtersFromQueryParams, defaultFilters) => {
        if (hasValue(filtersFromQueryParams) && filtersFromQueryParams.remove('kind').size > 0) {
            return filtersFromQueryParams;
        }

        return defaultFilters;
    },
);

export const activeFilters = createSelector(
    filtersFromQueryParamsOrDefault,
    (state: RootState) => state.lists.newList.filtersAndColumns.shownFilters,
    filterSections,
    (defaultFilters, shownFilters, filterSections) => {
        const defaultFilterKeys = defaultFilters.keySeq();
        const defaultFilterEntities = filterSections
            .valueSeq()
            .filter(section => {
                return section.entities.some(({id}) => defaultFilterKeys.includes(id));
            })
            .flatMap(section => section.entities.map(entity => entity.id));

        return shownFilters.isEmpty() ? Set(defaultFilterEntities) : shownFilters;
    },
);

export const filtersFormData = createSelector(
    activeFilters,
    filterSections,
    filtersFromQueryParamsOrDefault,
    (activeFilters, filterSections, filtersFromQueryParamsOrDefault): GenericFormData => {
        const shownFilterSectionsCount = filterSections.filter(section => activeFilters.includes(section.id)).size;

        return {
            fields: filterSections
                .toArray()
                .map(([, section]) => ({
                    fieldType: GenericFieldType.section,
                    label: section.name,
                    fields: section.entities
                        .filter(entity => activeFilters.includes(entity.id))
                        .toArray()
                        .map(entity => {
                            const isNamedSubsection = isDropdowns(entity.filter) && entity.values.size > 1;
                            return {
                                fieldType: GenericFieldType.element,
                                label: isNamedSubsection ? entity.name : '',
                                fields: getFilterSectionFields(entity, filtersFromQueryParamsOrDefault),
                                elementCSS: css`
                                    ${() => [
                                        entity.values.size > 1
                                            ? css`
                                                  & > label {
                                                      font-size: ${props =>
                                                          props.theme.fontConfiguration.sizes.Caption};
                                                      margin-bottom: ${getSpacing(s3)};
                                                  }
                                              `
                                            : '',
                                        isNamedSubsection
                                            ? css`
                                                  &&& {
                                                      margin-bottom: ${getSpacing(s4)};
                                                  }
                                              `
                                            : '',
                                    ]}
                                `,
                                props: {
                                    fieldHolderCss: css`
                                        display: flex;
                                        flex-direction: column;
                                    `,
                                    noSpacing: true,
                                },
                            };
                        }),
                    props: {
                        afterTitleElement: (
                            <Base
                                onClick={() =>
                                    Actions.hideFilterSection.dispatch(section.entities.map(entity => entity.id))
                                }
                            >
                                <Close size={12} />
                            </Base>
                        ),
                        css: css`
                            ${SectionTitle} {
                                ${AfterTitleElement} {
                                    display: ${shownFilterSectionsCount > 1 && PlatformHelper.isTouch()
                                        ? 'block'
                                        : 'none'};
                                }

                                ${addHover(css`
                                    &:hover {
                                        ${AfterTitleElement} {
                                            ${() => (shownFilterSectionsCount > 1 ? 'display: block;' : '')}
                                        }
                                    }
                                `)}
                            }
                        `,
                    },
                }))
                .filter(section => section.fields.length) as GenericFieldSection[],
        };
    },
);

const sectionColumnsToFormFields = (
    sectionColumns: List<Models.Column>,
    defaultColumns: Set<string>,
): GenericItem[] => {
    return sectionColumns
        .sortBy(section => section.name)
        .toArray()
        .map(column => ({
            fieldType: GenericFieldType.checkbox,
            name: column.id,
            text: column.description ? CheckboxLabelWithDescription(column.name, column.description) : column.name,
            value: defaultColumns.includes(column.id),
        }));
};

const CheckboxLabelWithDescription = (title: string, description: string) => {
    return (
        <Flex direction="column">
            <Body>{title}</Body>
            <Body secondary>{description}</Body>
        </Flex>
    );
};

const sortColumnSections = (a: GenericFieldSection, b: GenericFieldSection) => {
    // Place columns that don't belong to any section at the end
    if (a.name === Constants.UNDEFINED_COLUMN_SECTION) {
        return 1;
    } else {
        return sortByString(a.name || '', b.name || '');
    }
};

const columnsFromQueryParamsOrDefault = createSelector(
    (state: RootState) => state.lists.newList.filtersAndColumns.defaultColumns,
    (_: RootState, props: {columnsFromQueryParams?: Set<string>}) => props.columnsFromQueryParams,
    (defaultColumns, columnsFromQueryParams) => {
        if (columnsFromQueryParams && !columnsFromQueryParams?.isEmpty()) {
            return columnsFromQueryParams;
        }
        return defaultColumns;
    },
);

export const columnsFormData = createSelector(
    (state: RootState) => state.lists.newList.filtersAndColumns.columnSections,
    columnsFromQueryParamsOrDefault,
    (columnSections, columnsFromQueryParamsOrDefault): GenericFormData => {
        return {
            fields: columnSections
                .toArray()
                .map<GenericFieldSection>(([sectionName, sectionColumns]) => {
                    return {
                        fieldType: GenericFieldType.section,
                        name: sectionName,
                        label:
                            sectionName === Constants.UNDEFINED_COLUMN_SECTION ? i18next.t('list.other') : sectionName,
                        fields: sectionColumnsToFormFields(sectionColumns, columnsFromQueryParamsOrDefault),
                    };
                })
                .sort(sortColumnSections),
        };
    },
);

const selectedColumnIdsFromForm = (formValues: FormState['values']) => {
    if (!formValues || Object.keys(formValues).length === 0) {
        return null;
    }

    return Object.keys(formValues).flatMap(sectionName => {
        const formSection = formValues[sectionName];
        const selectedColumnIdsInSection = Object.keys(formSection).filter(columnId => formSection[columnId]);
        return selectedColumnIdsInSection;
    });
};

const columnsFormValues = getFormValues(Constants.COLUMNS_FORM);

export const selectedColumnIds = createSelector(
    columnsFormValues,
    columnsFromQueryParamsOrDefault,
    (columnsFormValues, columnsFromQueryParamsOrDefault) => {
        const selectedFormColumnIds = selectedColumnIdsFromForm(columnsFormValues);
        return List(selectedFormColumnIds?.length ? selectedFormColumnIds : columnsFromQueryParamsOrDefault);
    },
);

export const selectedColumns = immutableSelectorCreator(
    selectedColumnIds,
    (state: RootState) => state.lists.newList.filtersAndColumns.columnSections,
    (selectedColumnIds, columnSections) =>
        columnSections
            .toList()
            .flatMap(columnsInSection => columnsInSection.filter(column => selectedColumnIds.includes(column.id))),
);

interface FiltersObject {
    [entityId: string]: string;
}

const hasCheckboxGroup = (entityValue: {[x: string]: any}) => {
    return Object.values(entityValue).some(value => typeof value === 'boolean');
};

const selectedDropdownsInSection = (entityValue: {[x: string]: any}, entityId: string) => {
    const selectedDropdownValues = Object.keys(entityValue).map(entityItemId => entityValue[entityItemId] || '');
    const isEmpty = selectedDropdownValues.filter(v => v).length === 0;

    return {[entityId]: isEmpty ? '' : selectedDropdownValues.join(',')};
};

const selectedCheckboxesInSection = (entityValue: {[x: string]: any}, entityId: string) => {
    const selectedChecboxValues = Object.keys(entityValue)
        .filter(entityItemId => entityValue[entityItemId])
        .join(',');

    return {[entityId]: selectedChecboxValues};
};

const selectedFiltersInForm = (formValues?: FormState['values']) => {
    if (!formValues || Object.keys(formValues).length === 0) {
        return null;
    }

    return Object.keys(formValues)
        .filter(entityId => formValues[entityId])
        .reduce<FiltersObject>((activeFilters, entityId) => {
            const entityValue = formValues[entityId];

            // Special case for the `gender` filter.
            // If both `Boys` and `Girls` are checked,
            // the value must be `all`, not `f,m`.
            const isBoysFilterChecked = entityValue.m;
            const isGirlsFilterChecked = entityValue.f;
            if (entityId === 'gender' && isBoysFilterChecked && isGirlsFilterChecked) {
                activeFilters.gender = 'all';
                return activeFilters;
            }

            // Multiple inputs in a section
            if (typeof entityValue === 'object') {
                if (hasCheckboxGroup(entityValue)) {
                    return {...activeFilters, ...selectedCheckboxesInSection(entityValue, entityId)};
                }

                return {...activeFilters, ...selectedDropdownsInSection(entityValue, entityId)};
            } else if (typeof entityValue === 'string') {
                activeFilters[entityId] = entityValue;
                return activeFilters;
            }

            return activeFilters;
        }, {});
};

const filtersFormValues = getFormValues(Constants.FILTERS_FORM);

export const selectedFilters = createSelector(
    filtersFormValues,
    (state: RootState) => state.lists.newList.filtersAndColumns.defaultFilters,
    (filtersFormValues, defaultFilters) => {
        const selectedFormFilters = selectedFiltersInForm(filtersFormValues);
        return selectedFormFilters ? selectedFormFilters : defaultFilters.toObject();
    },
);
